import { Button, Stack, Typography } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFileOutlined';
import { downloadPaymentEvidence } from '../../api/payments';
import { downloadBlob } from '../../utils/downloadBlob';
import { formatFileSize } from '../../utils/paymentReceipts';
import { DocumentPreviewTarget } from '../DocumentPreviewDialog';

export interface EvidenceItem {
  id: number;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
  uploadedBy: string | null;
}

/** Every receipt uploaded for a payment (Phase 107), oldest first, each
 * with who uploaded it and when; clicking one opens it. Earlier uploads
 * stay listed after a replacement. */
export const PaymentEvidenceList = ({
  paymentId,
  evidence,
  onPreview,
}: {
  paymentId: number;
  evidence: EvidenceItem[];
  onPreview: (target: DocumentPreviewTarget) => void;
}) => {
  if (evidence.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" component="div">
        Payment receipt: Not uploaded
      </Typography>
    );
  }
  return (
    <Stack spacing={0} data-testid="payment-evidence">
      {evidence.map((e, i) => (
        <Stack key={e.id} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            startIcon={<AttachFileIcon />}
            sx={{ py: 0, textTransform: 'none', minWidth: 0 }}
            onClick={() =>
              onPreview({
                key: `evidence-${e.id}`,
                originalName: e.originalName,
                mimeType: e.mimeType,
                fetchBlob: async () => (await downloadPaymentEvidence(paymentId, e.id)).blob,
                onDownload: async () => {
                  const { blob, filename } = await downloadPaymentEvidence(paymentId, e.id);
                  downloadBlob(blob, filename);
                },
              })
            }
          >
            {e.originalName}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {formatFileSize(e.sizeBytes)} · uploaded by {e.uploadedBy ?? '—'} on {new Date(e.uploadedAt).toLocaleString()}
            {evidence.length > 1 && i < evidence.length - 1 ? ' · earlier upload' : ''}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};
